/* =========================================================================
   PDP quizzes — "Quiz Behaviour & PDP Recommendation Logic"

   One quiz per sub-category, one stored answer per quiz, on the fashion
   PDP (sections/main-product.liquid). The section decides which quiz a
   product answers (data-sq-kind / data-quiz-kind):

     top       Topwear      two quizzes: body type, then letter size
     bottom    Bottomwear   waist size (28, 30 … 48)
     footwear  Footwear     UK size (UK-5 … UK-13)

   No other product gets a quiz or a recommendation card.

   Stored on the shopper's personalization profile (assets/vro-
   personalization.js: guests in localStorage, signed-in shoppers under
   their customer id and the server copy) as one facet:

     facets.pdp_quiz = {
       top:    { value: 'L',    brand: 'Bamboology', at },
       body:   { value: 'oval', label: 'Oval', at },
       bottom: { value: '30',   brand: "Levi's", at },
       shoe:   { value: 'UK 8', brand: 'Griffin', at }
     }

   `brand` is the brand of the product the size was answered on, so the
   card can say "Because you wear an L in Bamboology".

   What a PDP shows, per the doc:
     quiz not attempted            the quiz (for Topwear, whichever of the
                                   two is still unanswered)
     just submitted, on this page  "Noted — you selected L & Oval", this
                                   page view only (never stored, so a
                                   reload, another PDP or coming back here
                                   shows the recommendation instead)
     answered, product matches     one card: "Recommended Size is L &
                                   Bodytype is Oval", or only the part that
                                   matches, with Retake
     answered, no match            nothing; the PDP is left as it was

   The Retake button is hidden while the quiz is open — it only belongs
   with an answer that has been given.

   Answers given before this file (the earlier combined size quiz, the
   home page's size quiz, an in-row body-type answer) are read as a
   fallback, so nobody is asked again for something they already said.
   ========================================================================= */
(function () {
  'use strict';
  if (window.VroPdpQuiz) return;

  var FACET = 'pdp_quiz';
  var OPEN_MS = 320;
  var reduceMotion = false;
  try { reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { /* old browser */ }

  /* Which stored answer each kind reads, and the questions it asks, in the
     order they are asked. */
  var SIZE_KEY = { top: 'top', letters: 'top', bottom: 'bottom', footwear: 'shoe' };
  var NEEDS = { top: ['body', 'top'], letters: ['top'], bottom: ['bottom'], footwear: ['shoe'] };

  /* This page view's just-submitted answers. Memory only, on purpose. */
  var noted = null;

  function profile() {
    return (window.VroPersonalization && window.VroPersonalization.profile) || null;
  }

  function cap(v) { v = String(v || ''); return v.charAt(0).toUpperCase() + v.slice(1); }

  function escapeHtml(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* "an L", "an XL", "an M", "an S", "an 8" — "a 30", "a UK 8", "a 2XL". */
  function article(size) {
    var s = String(size || '').trim();
    return /^(XS|S|M|L|XL|XXL|XXXL|X|F|H|N|R|8|11|18)\b/i.test(s) && !/^UK/i.test(s) ? 'an' : 'a';
  }

  /* ---------------- Sizes ---------------- */

  function letter(v) {
    var s = String(v || '').toUpperCase().replace(/[\s\-_.]/g, '');
    var map = { XXS: '2XS', XXL: '2XL', XXXL: '3XL', XXXXL: '4XL', XXXXXL: '5XL' };
    return map[s] || s;
  }

  function shoeNumber(v) {
    var m = String(v || '').trim().match(/^(?:U\.?K\.?)?\s*-?\s*(\d{1,2}(?:\.5)?)$/i);
    return m ? parseFloat(m[1]) : null;
  }

  /* How an answer reads in a sentence: a shoe size is "UK 5" whichever
     way the chip spells it ("UK-5", "UK 5", "5"). */
  function pretty(key, v) {
    if (key === 'shoe') {
      var n = shoeNumber(v);
      if (n !== null) return 'UK ' + n;
    }
    return String(v || '');
  }

  function waistNumber(v) {
    var m = String(v || '').trim().match(/^W?\s*(\d{2})$/i);
    return m ? parseInt(m[1], 10) : null;
  }

  /* The product's own spelling of the shopper's answer, or null when the
     product does not come in it. */
  function matchSize(key, answer, productSizes) {
    if (!answer) return null;
    for (var i = 0; i < productSizes.length; i++) {
      var p = productSizes[i];
      if (key === 'top') {
        if (letter(p) && letter(p) === letter(answer)) return p;
      } else if (key === 'bottom') {
        var a = waistNumber(answer);
        if (a !== null && waistNumber(p) === a) return p;
      } else if (key === 'shoe') {
        var n = shoeNumber(answer);
        if (n !== null && shoeNumber(p) === n) return p;
      }
    }
    return null;
  }

  /* ---------------- Stored answers ---------------- */

  function readStored() {
    var p = profile();
    var v = p && p.getFacet ? p.getFacet(FACET) : null;
    return (v && typeof v === 'object') ? v : {};
  }

  /* Answers the shopper gave somewhere else before this existed. */
  function fallbacks() {
    var out = {};
    var p = profile();
    if (!p || !p.get) return out;
    var facets;
    try { facets = (p.get() || {}).facets || {}; } catch (e) { return out; }

    var mem = facets.size_memory;
    if (mem && typeof mem === 'object') {
      if (mem.alpha && mem.alpha.value && mem.alpha.source === 'quiz') out.top = { value: mem.alpha.value, brand: '' };
      if (mem.numeric && mem.numeric.value && mem.numeric.source === 'quiz') out.bottom = { value: mem.numeric.value, brand: '' };
    }
    Object.keys(facets).forEach(function (k) {
      var base = k.split('@')[0];
      var v = facets[k];
      if (typeof v !== 'string' || !v) return;
      if (base === 'size') {
        if (!out.top && /^[XSML0-9]+$/i.test(letter(v)) && !/^\d+$/.test(v)) out.top = { value: v, brand: '' };
        else if (!out.bottom && waistNumber(v) !== null) out.bottom = { value: v, brand: '' };
      }
      if (base === 'body' && !out.body) out.body = { value: v.toLowerCase(), label: cap(v) };
    });
    return out;
  }

  function answers() {
    var stored = readStored();
    var fb = fallbacks();
    var out = {};
    ['top', 'body', 'bottom', 'shoe'].forEach(function (k) {
      var v = stored[k];
      if (v && v.value) out[k] = v;
      else if (fb[k] && fb[k].value) out[k] = fb[k];
    });
    return out;
  }

  function save(given, brand) {
    var p = profile();
    var now = Date.now();
    var next = JSON.parse(JSON.stringify(readStored()));
    Object.keys(given).forEach(function (k) {
      var entry = { value: given[k].value, at: now };
      if (k === 'body') entry.label = given[k].label || cap(given[k].value);
      else entry.brand = brand || '';
      next[k] = entry;
    });
    if (p && p.setFacet) {
      p.setFacet(FACET, next);
      /* The rest of the shop reads body type from 'body'. */
      if (given.body) p.setFacet('body', given.body.value);
    }
    /* Size memory keeps listing filters and the profile page in step.
       Letters and waist numbers only; it has no shoe scale. */
    if (window.VroSizeMemory && window.VroSizeMemory.remember) {
      if (given.top) window.VroSizeMemory.remember(given.top.value, 'quiz', brand || '');
      if (given.bottom) window.VroSizeMemory.remember(given.bottom.value, 'quiz', brand || '');
    }
  }

  /* ---------------- One recommended size, anywhere ----------------

     The size to recommend on a product, and why — the same answer on the
     product page's card and in the Choose options pop-up the (+) on a
     product card opens (assets/quick-add.js):

       their answer to this kind's size quiz (Topwear/letters, waist,
       UK shoe size), with the brand they answered it on — and nothing
       else. A size picked on a product or ticked in a filter is not a
       recommendation; before the quiz is taken the card asks instead.

     A quiz answer the product does not come in means nothing is
     recommended. */
  function recommendSize(kind, sizes, brand) {
    var key = SIZE_KEY[kind];
    if (!key || !sizes || !sizes.length) return null;
    var a = answers()[key];
    if (a && a.value) {
      var hit = matchSize(key, a.value, sizes);
      if (!hit) return null;
      return { value: hit, shown: key === 'shoe' ? pretty('shoe', a.value) : hit, brand: a.brand || '', source: 'quiz' };
    }
    /* No quiz answer, no recommendation: a size picked on a product
       page is a variant chosen, not a size the shopper told us. */
    return null;
  }

  /* The sentence under it. */
  function reasonFor(rec) {
    if (!rec) return '';
    var s = rec.shown;
    if (rec.brand) return 'Because you wear ' + article(s) + ' ' + s + ' in ' + rec.brand + ' this will fit you perfectly';
    if (rec.source === 'filter') return 'Because you shopped size ' + s + ' earlier, this should fit you well';
    if (rec.source === 'quiz') return 'Because you selected ' + s + ' in the quiz, this will fit you perfectly';
    return 'Because you picked ' + s + ' before, this should fit you well';
  }

  /* Which quiz a product answers, read from its /products/<handle>.js
     data the way sections/main-product.liquid reads it from Liquid:
     Footwear by its tag (or, untagged, a shoe product type); Bottomwear
     sized in waist numbers; Topwear sized in letters; anything else sized
     in letters. '' for everything else. */
  var SHOE_TYPE_WORDS = ['shoe', 'boot', 'sandal', 'floater', 'slipper', 'sneaker', 'loafer', 'flip flop', 'flip-flop', 'clog', 'moccasin', 'chappal', 'jutti', 'mojari'];
  var BOTTOM_WORDS = ['trouser', 'pant', 'jean', 'short', 'chino', 'cargo', 'pyjama', 'pajama', 'jogger', 'legging', 'skirt', 'lower'];
  var TOP_WORDS = ['shirt', 'tee', 'polo', 'jacket', 'blazer', 'coat', 'sweater', 'sweatshirt', 'hoodie', 'kurta', 'jersey', 'pullover', 'cardigan', 'waistcoat', 'suit'];
  var ALPHA_SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '2XL', '3XL', '4XL', '5XL'];

  function sizeOptionOf(product) {
    var opts = (product && product.options) || [];
    for (var i = 0; i < opts.length; i++) {
      var o = opts[i];
      var name = String((o && typeof o === 'object') ? o.name : o || '').toLowerCase().trim();
      if (name === 'size' || name === 'sizes') {
        var values = (o && typeof o === 'object' && o.values) ? o.values : [];
        if (!values.length) {
          (product.variants || []).forEach(function (v) {
            var val = (v.options || [v.option1, v.option2, v.option3])[i];
            if (val && values.indexOf(val) === -1) values.push(val);
          });
        }
        return { index: i, values: values };
      }
    }
    return null;
  }

  function kindFor(product) {
    var opt = sizeOptionOf(product);
    if (!opt || !opt.values.length) return '';
    var tags = (product.tags || []).map(function (t) { return String(t).toLowerCase().trim(); });
    var has = function (t) { return tags.indexOf(t) > -1; };
    var type = String(product.type || '').toLowerCase();
    var any = function (hay, words) { for (var i = 0; i < words.length; i++) if (hay.indexOf(words[i]) > -1) return true; return false; };

    var shoe = has('footwear') || (!has('topwear') && !has('bottomwear') && any(type, SHOE_TYPE_WORDS));
    if (shoe) return 'footwear';

    var alpha = false;
    var numeric = false;
    opt.values.forEach(function (v) {
      var u = String(v).toUpperCase().replace(/\s+/g, '');
      if (/^\d+$/.test(u)) { var n = parseInt(u, 10); if (n >= 24 && n <= 50) numeric = true; }
      else if (ALPHA_SIZES.indexOf(u) > -1) alpha = true;
    });

    var hay = (type + ' ' + String(product.title || '') + ' ' + tags.join(' ')).toLowerCase();
    var garment = any(hay, BOTTOM_WORDS) ? 'bottom' : (any(hay, TOP_WORDS) ? 'top' : '');
    var top = has('topwear');
    var bottom = has('bottomwear');
    if (top && bottom) { if (garment === 'top') bottom = false; else top = false; }
    else if (!top && !bottom) { top = garment === 'top'; bottom = garment === 'bottom'; }

    if (bottom && numeric) return 'bottom';
    if (top && alpha) return 'top';
    /* Only Topwear, Bottomwear and Footwear get a recommendation. */
    return '';
  }

  /* For a product's /products/<handle>.js data: { index, value, shown,
     brand, source, reason } or null. */
  function recommendFor(product) {
    var kind = kindFor(product);
    if (!kind) return null;
    var opt = sizeOptionOf(product);
    var rec = recommendSize(kind, opt.values, product.vendor || '');
    if (!rec) return null;
    rec.index = opt.index;
    rec.kind = kind;
    rec.reason = reasonFor(rec);
    return rec;
  }

  /* ---------------- The recommendation card ---------------- */

  function fitCard() { return document.querySelector('[data-pdp-fit][data-quiz-kind]'); }
  function quizCard() { return document.querySelector('[data-pdp-size-quiz][data-sq-kind]'); }

  function bodySuits(v) {
    var holder = document.querySelector('[data-pdp-body-suits]');
    var list = holder ? String(holder.getAttribute('data-pdp-body-suits') || '') : '';
    return !!v && list.indexOf('|' + String(v).toLowerCase() + '|') > -1;
  }

  function fill(tpl, tokens, bold) {
    return String(tpl || '').replace(/\[(size|body|brand|answer|a)\]/g, function (m, name) {
      var val = tokens[name] == null ? '' : String(tokens[name]);
      if (bold && (name === 'size' || name === 'body' || name === 'answer')) return '<b>' + escapeHtml(val) + '</b>';
      return bold ? escapeHtml(val) : val;
    }).replace(/\s{2,}/g, ' ').trim();
  }

  function setRetake(card, visible) {
    Array.prototype.forEach.call(card.querySelectorAll('[data-sq-retake]'), function (b) {
      b.style.display = visible ? '' : 'none';
      b.setAttribute('aria-hidden', visible ? 'false' : 'true');
    });
  }

  function quizOpen() {
    var q = quizCard();
    return !!(q && q.classList.contains('is-quiz-open'));
  }

  function renderFit() {
    var card = fitCard();
    if (!card) return;
    var kind = card.getAttribute('data-quiz-kind');
    var sizeKey = SIZE_KEY[kind];
    if (!sizeKey) return;
    var sizes = (card.getAttribute('data-product-sizes') || '').split('|').filter(Boolean);
    var title = card.querySelector('[data-pdp-fit-title]');
    var text = card.querySelector('[data-pdp-fit-text]');
    var copy = function (k, d) { return card.getAttribute('data-copy-' + k) || d; };

    var headingHtml = '';
    var line = '';

    if (noted) {
      var bits = [];
      if (noted[sizeKey]) bits.push(pretty(sizeKey, noted[sizeKey].value));
      if (noted.body) bits.push(noted.body.label || cap(noted.body.value));
      /* Each answer in bold on its own: "you selected **L** & **Oval**". */
      headingHtml = fill(copy('noted-title', 'Noted — you selected [answer]'), { answer: '\u0000' }, true)
        .replace('<b>\u0000</b>', bits.map(function (b) { return '<b>' + escapeHtml(b) + '</b>'; }).join(' &amp; '));
      line = copy('noted-text', '');
    } else {
      var a = answers();
      /* Their quiz answer for this kind, or — not answered yet — the size
         they last picked while shopping (recommendSize above). */
      var rec = recommendSize(kind, sizes, card.getAttribute('data-brand') || '');
      var hit = rec ? rec.value : null;
      var bodyAns = kind === 'top' ? a.body : null;
      var bodyHit = !!(bodyAns && bodySuits(bodyAns.value));
      if (!hit && !bodyHit) {
        card.hidden = true;
        return;
      }
      /* What they said, as this product spells it — except a shoe size,
         where "UK 8" reads better than a bare "8". */
      var shown = rec ? rec.shown : '';
      var tokens = {
        size: shown,
        a: article(shown),
        body: bodyAns ? (bodyAns.label || cap(bodyAns.value)) : '',
        brand: rec ? (rec.brand || '') : ''
      };
      if (hit && bodyHit) headingHtml = fill(copy('both-title', 'Recommended Size is [size] & Bodytype is [body]'), tokens, true);
      else if (hit) headingHtml = fill(copy('size-title', 'Recommended Size is [size]'), tokens, true);
      else headingHtml = fill(copy('body-title', 'Recommended Bodytype is [body]'), tokens, true);

      /* A quiz answer reads as the PDP prompts sheet has it ("Because you
         picked S in the size quiz, this should fit you well."), whatever
         brand it was answered on; the brand line is for sizes picked while
         shopping. */
      if (hit && rec.source === 'quiz' && bodyHit) line = fill(copy('both-text', 'Because you selected [size] size and [body] body type in the quiz, this will suit you perfectly'), tokens, false);
      else if (hit && rec.source === 'quiz') line = fill(copy('size-text', 'Because you picked [size] in the size quiz, this should fit you well.'), tokens, false);
      else if (hit && tokens.brand) line = fill(copy('brand-text', 'Because you wear [a] [size] in [brand] this will fit you perfectly'), tokens, false);
      else if (hit) line = reasonFor(rec);
      else line = fill(copy('body-text', 'Because you selected [body] body type in the quiz, this will suit you perfectly'), tokens, false);
    }

    var tick = card.querySelector('[data-pdp-fit-check]');
    if (tick) tick.hidden = !noted;
    if (title) title.innerHTML = headingHtml;
    if (text) { text.textContent = line; text.hidden = !line; }
    setRetake(card, !quizOpen());
    card.hidden = false;
  }

  /* ---------------- The quiz card ---------------- */

  function init(card) {
    if (!card || card.__vroSq) return;
    card.__vroSq = true;

    var kind = card.getAttribute('data-sq-kind');
    var needs = NEEDS[kind];
    var sizeKey = SIZE_KEY[kind];
    if (!needs) return;

    var intro = card.querySelector('[data-sq-intro]');
    var introText = card.querySelector('[data-sq-intro-text]');
    var introDefault = introText ? introText.textContent : '';
    var done = card.querySelector('[data-sq-done]');
    var panel = card.querySelector('[data-sq-panel]');
    var progress = card.querySelector('[data-sq-progress]');
    var back = card.querySelector('[data-sq-back]');
    var stepEls = {
      body: card.querySelector('[data-sq-step="body"]'),
      size: card.querySelector('[data-sq-step="size"]')
    };
    var brand = card.getAttribute('data-brand') || '';
    var flow = [];
    var at = 0;
    var given = {};
    var closing = null;

    if (done) done.hidden = true;

    function stepOf(key) { return key === 'body' ? 'body' : 'size'; }

    function missing() {
      var a = answers();
      return needs.filter(function (k) { return !a[k]; });
    }

    function openers() {
      return Array.prototype.slice.call(card.querySelectorAll('[data-sq-open]'));
    }

    function paint() {
      var a = answers();
      var body = (given.body && given.body.value) || (a.body && a.body.value) || '';
      var size = (given[sizeKey] && given[sizeKey].value) || (a[sizeKey] && a[sizeKey].value) || '';
      Array.prototype.forEach.call(card.querySelectorAll('[data-sq-body]'), function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-sq-body') === body ? 'true' : 'false');
      });
      Array.prototype.forEach.call(card.querySelectorAll('[data-sq-size]'), function (b) {
        var v = b.getAttribute('data-sq-size');
        var on = !!size && (v === size || (sizeKey === 'top' && letter(v) === letter(size)) ||
          (sizeKey === 'shoe' && shoeNumber(v) !== null && shoeNumber(v) === shoeNumber(size)) ||
          (sizeKey === 'bottom' && waistNumber(v) !== null && waistNumber(v) === waistNumber(size)));
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.classList.toggle('is-selected', on);
      });
    }

    function showStep(i, direction) {
      at = i;
      var name = stepOf(flow[i]);
      Object.keys(stepEls).forEach(function (key) {
        var el = stepEls[key];
        if (!el) return;
        var on = key === name;
        el.hidden = !on;
        el.classList.remove('is-entering', 'is-entering-back');
        if (on && !reduceMotion) {
          void el.offsetWidth;
          el.classList.add(direction === 'back' ? 'is-entering-back' : 'is-entering');
        }
      });
      if (progress) {
        progress.textContent = (i + 1) + ' of ' + flow.length;
        progress.style.visibility = flow.length > 1 ? '' : 'hidden';
      }
      if (back) back.hidden = i === 0;
      paint();
    }

    /* The card at rest: gone once every question it asks is answered,
       otherwise the invitation, worded for what is left. */
    function settle() {
      if (card.classList.contains('is-quiz-open')) return;
      var left = missing();
      if (!left.length) { card.hidden = true; return; }
      if (introText) {
        var t = introDefault;
        if (kind === 'top' && left.length === 1) {
          t = left[0] === 'body'
            ? (card.getAttribute('data-text-body-only') || t)
            : (card.getAttribute('data-text-size-only') || t);
        }
        introText.textContent = t;
      }
      if (intro) intro.hidden = false;
      if (panel) { panel.hidden = true; panel.classList.remove('is-open'); }
      card.hidden = false;
    }

    function open(full) {
      if (closing) { clearTimeout(closing); closing = null; }
      given = {};
      var left = missing();
      /* Retake asks everything again, with the old answers shown picked so
         it is a correction rather than a blank form. */
      flow = (full || !left.length) ? needs.slice() : left;
      card.hidden = false;
      if (intro) intro.hidden = true;
      if (done) done.hidden = true;
      panel.hidden = false;
      card.classList.add('is-quiz-open');
      openers().forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      var fc = fitCard();
      if (fc) setRetake(fc, false);
      showStep(0);
      if (reduceMotion) { panel.classList.add('is-open'); }
      else {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { panel.classList.add('is-open'); });
        });
      }
      var first = stepEls[stepOf(flow[0])] && stepEls[stepOf(flow[0])].querySelector('button');
      if (first) setTimeout(function () { try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); } }, 60);
    }

    function close() {
      panel.classList.remove('is-open');
      card.classList.remove('is-quiz-open');
      openers().forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      var after = function () {
        closing = null;
        panel.hidden = true;
        settle();
        renderFit();
      };
      if (reduceMotion) after();
      else closing = setTimeout(after, OPEN_MS);
    }

    function pickOnProduct() {
      var fc = fitCard();
      var sizes = fc ? (fc.getAttribute('data-product-sizes') || '').split('|').filter(Boolean) : [];
      var value = given[sizeKey] && matchSize(sizeKey, given[sizeKey].value, sizes);
      if (!value) return;
      var group = document.querySelector('[data-pdp-size-option]');
      if (!group) return;
      var inputs = group.querySelectorAll('input.option-swatch');
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].value === value) {
          if (!inputs[i].checked && !inputs[i].disabled) {
            inputs[i].checked = true;
            inputs[i].dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }
      }
    }

    function finish() {
      save(given, brand);
      noted = JSON.parse(JSON.stringify(given));
      pickOnProduct();
      try {
        document.dispatchEvent(new CustomEvent('vro:size-quiz:done', {
          detail: {
            kind: kind,
            body: given.body ? given.body.value : null,
            size: given[sizeKey] ? given[sizeKey].value : null,
            brand: brand
          }
        }));
      } catch (e) { /* old browser */ }
      close();
    }

    function advance() {
      if (at + 1 < flow.length) showStep(at + 1);
      else finish();
    }

    card.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest('[data-sq-open]')) { e.preventDefault(); open(false); return; }
      if (t.closest('[data-sq-close]')) { e.preventDefault(); close(); return; }
      if (t.closest('[data-sq-back]')) { e.preventDefault(); if (at > 0) showStep(at - 1, 'back'); return; }

      var bodyBtn = t.closest('[data-sq-body]');
      if (bodyBtn && flow[at] === 'body') {
        e.preventDefault();
        given.body = { value: bodyBtn.getAttribute('data-sq-body'), label: bodyBtn.getAttribute('data-sq-label') || cap(bodyBtn.getAttribute('data-sq-body')) };
        paint();
        /* A beat so the pick is seen before the next question slides in. */
        setTimeout(advance, reduceMotion ? 0 : 220);
        return;
      }

      var sizeBtn = t.closest('[data-sq-size]');
      if (sizeBtn && flow[at] === sizeKey) {
        e.preventDefault();
        given[sizeKey] = { value: String(sizeBtn.getAttribute('data-sq-size') || '').trim() };
        paint();
        setTimeout(advance, reduceMotion ? 0 : 220);
      }
    });

    card.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && card.classList.contains('is-quiz-open')) close();
    });

    /* Retake, on the recommendation card: bring the quiz back where the
       shopper can see it and ask it again. */
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('[data-sq-retake]') : null;
      if (!t) return;
      e.preventDefault();
      open(true);
      try { card.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' }); } catch (err) { /* ignore */ }
    });

    card.__vroSettle = settle;
    card.__vroClose = close;
    settle();
  }

  function renderAll() {
    var q = quizCard();
    if (q && q.__vroSettle) q.__vroSettle();
    renderFit();
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-pdp-size-quiz][data-sq-kind]'), init);
    renderFit();
  }

  window.VroPdpQuiz = {
    init: initAll, render: renderAll, answers: answers, matchSize: matchSize,
    kindFor: kindFor, recommendFor: recommendFor, recommendSize: recommendSize
  };

  /* vro-personalization.js is deferred and loads after this section's
     script, so wait for the document: every deferred script has run by
     DOMContentLoaded, and the profile is read from localStorage at once. */
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  window.addEventListener('load', renderAll);
  document.addEventListener('vro:personalization:ready', renderAll);
  /* An answer from another tab, a sign-in, or the server copy. */
  document.addEventListener('vro:profile:change', function () { if (!quizOpen()) renderAll(); });
  document.addEventListener('shopify:section:load', initAll);

  /* Back/forward cache: the page comes back as it was left, "Noted"
     included. Coming back is not submitting, so show the recommendation. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    noted = null;
    var q = quizCard();
    if (q && q.classList.contains('is-quiz-open') && q.__vroClose) q.__vroClose();
    renderAll();
  });
})();
